import React, { useCallback, useMemo, useRef, useState } from 'react';

import saveFile from 'helpers/saveFile';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import {
  TriStateCheckbox,
  TriStateCheckboxChangeEvent
} from 'primereact/tristatecheckbox';
import { classNames } from 'primereact/utils';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import { useDispatch, useSelector } from 'react-redux';
import { setLicenseInfo } from 'state/license/licenseSlice';

import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import { ImportErrorDialog } from 'ui/shared/ImportErrorDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { UsersTemplate } from '../../constants';
import { isSelected } from '../../helpers';
import { useDebounce } from '../../hooks';
import { useApiCall } from '../../hooks/useApiCall';
import { AppDispatch, RootState } from '../../state/store';
import { BaseFilter, Employee, PageRequest } from '../../types/interfaces';
import EmployeeCard from '../../ui/EmployeeCard/EmployeeCard';
import pageSizeValues from '../../ui/shared/paginator/pageSizeValues';
import { clearFilter } from '../../utils';
import VisibilityWrapper from '../../utils/wrappers/VisibilityWrapper';
import { EmployeesService, LicenseService } from '../services';

import '../../ui/css/EmployeesTable.css';
import EmployeeProductivityCalcDialog from './EmployeeProductivityCalcDialog';

export default function EmployeesTable() {
  const licenseInfo = useSelector((state: RootState) => state.license.info);
  const dispatch = useDispatch<AppDispatch>();
  const usersLimitText = useMemo(() => {
    const current = licenseInfo?.currentUsers;
    const max = licenseInfo?.maxUsers;
    if (current === undefined || max === undefined) return null;
    return `Пользователей: ${current}/${max}`;
  }, [licenseInfo?.currentUsers, licenseInfo?.maxUsers]);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [productivityCalcVisible, setProductivityCalcVisible] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ error: string }[]>(
    []
  );
  const [errorDialogVisible, setErrorDialogVisible] = useState<boolean>(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<
    string | number | null
  >(null);
  const [selectedEmployees, setSelectedEmployees] =
    useState<Employee>(UsersTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    personnelNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    fullName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    phone: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    isUser: { value: null, matchMode: FilterMatchMode.EQUALS },
    login: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [loading, setLoading] = useState<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emailFilterRef = useRef<HTMLInputElement>(null);
  const employeeCardRef = useRef<null | { handleCancel: () => void }>(null);
  const loginFilterRef = useRef<HTMLInputElement>(null);
  const nameFilterRef = useRef<HTMLInputElement>(null);
  const personnelNumberFilterRef = useRef<HTMLInputElement>(null);
  const phoneFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);

  const isSelectedIdExist = isSelected(selectedEmployees);

  const getEmployees = (data: Employee[]) => {
    return [...(data || [])].map((d) => {
      return d;
    });
  };

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(
    pageSizeValues[pageSizeValues.length - 1].value
  );
  const [totalCount, setTotalCount] = useState(1);
  const onPageSizeChanged = useCallback(async (size: number) => {
    setPageSize(size);
  }, []);

  const getEmployeesCallback = useCallback(async () => {
    setLoading(true);
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const filtersForApi: PageRequest = {
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify(
        Object.keys(filters).map((x): BaseFilter => {
          const f = filters[x];
          return {
            field: x,
            value: (f as DataTableFilterMetaData).value,
            operator: (f as DataTableFilterMetaData).matchMode
          };
        })
      )
    };

    const result = (await EmployeesService.getEmployees(filtersForApi)).data;
    setEmployees(getEmployees(result?.value.itemsList ?? []));
    setTotalCount((result.value.total === 0 ? 1 : result.value.total) ?? 1);
    setLoading(false);
  }, [filters, multiSortMeta, pageNumber, pageSize]);
  useApiCall(getEmployeesCallback);

  const handleProductivityCalculationSuccess = useCallback(async () => {
    await getEmployeesCallback();
    toast.current?.show({
      severity: 'success',
      summary: 'Расчет завершен',
      detail: 'Производительность сотрудников обновлена',
      life: 3000
    });
  }, [getEmployeesCallback]);

  const handleProductivityCalculationError = useCallback((detail: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Ошибка',
      detail,
      life: 3000
    });
  }, []);

  const exportTemplate = useCallback(async () => {
    const result = await EmployeesService.getEmployeesTemplateExcel();
    await saveFile(result.data, 'Шаблон Сотрудники.xls', '');
  }, []);

  const importEmployeesXLS = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Validate the file
      const validationResponse = await EmployeesService.importEmployees(
        formData,
        true
      );
      if (validationResponse?.data?.errors?.length > 0) {
        setValidationErrors(
          validationResponse.data.errors.map((i) => ({ error: i }))
        );
        setErrorDialogVisible(true);
        return;
      }

      // Import the file (PUT)
      const importResponse = await EmployeesService.importEmployees(formData);
      toast.current?.show({
        severity: 'success',
        summary: 'Импорт успешно завершен',
        detail: `${importResponse.data.employees.length} cотрудников импортировано.`,
        sticky: true
      });

      await getEmployeesCallback();
    } catch (error) {
      console.error('Ошибка импорта:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка импорта',
        detail: 'Произошла ошибка при импорте файла.',
        sticky: true
      });
    }
  };

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
    setPageNumber(1);
    setFirst(0);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    setPageNumber(1);
    setFirst(0);

    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    600
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const handleDeleteConfirmation = async () => {
    handleDelete(employeeToDelete);
    setDeleteDialogVisible(false);
  };

  const userBodyTemplate = (rowData: Employee) => {
    return (
      <i
        className={classNames('pi', {
          'true-icon pi-check-circle': rowData.isUser,
          'false-icon pi-times-circle': !rowData.isUser
        })}
      ></i>
    );
  };

  const userRowFilterTemplate = useMemo(() => {
    const filter = (filters['isUser'] as DataTableFilterMetaData).value;
    return (
      <TriStateCheckbox
        value={filter === 'true' ? true : filter === 'false' ? false : null}
        onChange={(e: TriStateCheckboxChangeEvent) => {
          const value =
            e.value === true ? 'true' : e.value === false ? 'false' : undefined;
          const _filters = JSON.parse(JSON.stringify(filters));
          _filters['isUser'].value = value;
          setFilters(_filters);
          setPageNumber(1);
          setFirst(0);
        }}
      />
    );
  }, [filters]);

  const handleUpdate = async (
    id: string | number,
    employee: Employee,
    twoFactorEnabled: boolean | undefined,
    isUserCreated: boolean | undefined
  ) => {
    if (employee.isUser && isUserCreated && toast.current && twoFactorEnabled) {
      toast.current.show({
        severity: 'success',
        summary: 'Пользователь добавлен',
        detail:
          'Код для завершения регистрации пользователя отправлен на указанную в карточке сотрудника почту',
        life: 3000
      });
    } else {
      toast.current?.show({
        severity: 'success',
        summary: 'Изменения сохранены',
        detail: 'Профиль пользователя успешно обновлен',
        life: 3000
      });
    }

    await getEmployeesCallback();
  };

  const handleDelete = async (id: string | number | null) => {
    if (id != null) await EmployeesService.deleteEmployee([id as number]);
    setSelectedEmployees(UsersTemplate);
    await getEmployeesCallback();
    if (selectedEmployees?.isUser) {
      try {
        const licenseResp = await LicenseService.getLicense();
        dispatch(setLicenseInfo(licenseResp.data));
      } catch (e) {}
    }
  };

  const handleAdd = async (
    employee: Employee,
    twoFactorEnabled: boolean | undefined
  ) => {
    if (employee.isUser && toast.current && twoFactorEnabled) {
      toast.current.show({
        severity: 'success',
        summary: 'Пользователь добавлен',
        detail:
          'Код для завершения регистрации пользователя отправлен на указанную в карточке сотрудника почту',
        life: 3000
      });
    } else {
      toast.current?.show({
        severity: 'success',
        summary: 'Пользователь добавлен',
        detail: 'Пользователь успешно создан',
        life: 3000
      });
    }

    await getEmployeesCallback();
  };

  const closeDialog = () => {
    // Function to close the dialog in parent
    setDialogVisible(false);
  };

  const onHideEmployeeCard = () => {
    if (employeeCardRef.current !== null) {
      employeeCardRef.current.handleCancel();
    }
  };

  const addButtonHandle = () => {
    setSelectedEmployees(UsersTemplate);
    setDialogVisible(true);
  };

  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите сотрудника для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  };

  const deleteButtonHandle = () => {
    // If a valid employee is selected, render the deletion confirmation dialog.
    if (isSelectedIdExist) {
      setEmployeeToDelete(selectedEmployees.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите сотрудника для удаления',
        life: 3000
      });
    }
  };

  const header = (
    <TablePageHeader
      tableName="employee-table"
      onGlobalSearchChange={(value) =>
        handleInputChange('global')({
          target: { value }
        } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
      customButtonsRight={[
        usersLimitText ? (
          <span key="users-limit" className="text-600 text-sm mr-2">
            {usersLimitText}
          </span>
        ) : null,
        <input
          key="input"
          type="file"
          accept="csv"
          onChange={importEmployeesXLS}
          style={{ visibility: 'hidden', width: 0 }}
          ref={fileInputRef}
        />,
        <Button
          key="export-template-button"
          className="employee-exportTemplate-btn__roles"
          type="button"
          severity="secondary"
          icon="pi pi-file-excel"
          rounded
          onClick={exportTemplate}
          tooltip="Выгрузка актуального шаблона импорта"
        />,
        <Button
          key="import-button"
          className="employee-import-btn__roles"
          type="button"
          severity="secondary"
          icon="pi pi-file-import"
          rounded
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            fileInputRef.current?.click();
          }}
          label="Импорт"
        />
      ]}
    >
      <Button
        type="button"
        icon="pi pi-calculator"
        rounded
        severity="secondary"
        className="employee-table-productivity-calc-btn__roles"
        tooltip="Автоматический расчет производительности сотрудников"
        onClick={() => setProductivityCalcVisible(true)}
      />
    </TablePageHeader>
  );

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  const [first, setFirst] = useState(0);
  const onPageChanged = useCallback(async (event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setFirst(0);
      setPageSize(event.rows);
    }

    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  return (
    <div className="employees-table wrapper wrapper--main">
      <EmployeeProductivityCalcDialog
        visible={productivityCalcVisible}
        onHide={() => setProductivityCalcVisible(false)}
        onCalculationSuccess={handleProductivityCalculationSuccess}
        onCalculationError={handleProductivityCalculationError}
        tableEmployees={employees}
      />
      <Toast ref={toast} />

      <ImportErrorDialog
        errorDialogVisible={errorDialogVisible}
        setErrorDialogVisible={setErrorDialogVisible}
        validationErrors={validationErrors}
      />

      <Dialog
        header="Карточка сотрудника"
        visible={dialogVisible}
        style={{ minWidth: '1290px', width: '1290px', minHeight: '520px' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        maximizable
        modal
        contentStyle={{ minHeight: '300px' }}
        onHide={() => onHideEmployeeCard()}
      >
        <VisibilityWrapper code="employee-card__roles">
          <EmployeeCard
            employeeProps={selectedEmployees}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={employeeCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="сотрудника"
        entityName={selectedEmployees?.fullName}
        warnings={[
          <span key="w1">Сотрудник может быть назначен на наряд.</span>
        ]}
      />
      <DataTable
        filters={filters}
        paginator
        first={first}
        rows={pageSize}
        onPage={onPageChanged}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${employees.length > 0 ? totalCount : 0}`}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedEmployees!}
        onSelectionChange={(e) => setSelectedEmployees(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedEmployees(e.data as Employee);
        }}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={employees}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        globalFilterFields={[
          'personnelNumber',
          'fullName',
          'phone',
          'email',
          'isUser',
          'login'
        ]}
        header={header}
        emptyMessage="Сотрудники не найдены."
        onRowReorder={(e) => setEmployees(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
      >
        <Column
          columnKey="personnelNumber"
          sortable
          field="personnelNumber"
          header="Табельный номер"
          filter
          onFilterClear={() =>
            clearFilter(
              'personnelNumber',
              filters,
              setFilters,
              personnelNumberFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={personnelNumberFilterRef}
                onChange={handleInputChange('personnelNumber')}
                placeholder="Поиск по табельному номеру"
              />
            </div>
          }
          style={{ width: '17%' }}
        />
        <Column
          columnKey="fullName"
          sortable
          field="fullName"
          header="ФИО"
          filter
          onFilterClear={() =>
            clearFilter(
              'fullName',
              filters,
              setFilters,
              nameFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={nameFilterRef}
                onChange={handleInputChange('fullName')}
                placeholder="Поиск по имени"
              />
            </div>
          }
          style={{ width: '25%' }}
        />
        <Column
          columnKey="phone"
          sortable
          field="phone"
          header="Телефон"
          filter
          onFilterClear={() =>
            clearFilter(
              'phone',
              filters,
              setFilters,
              phoneFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={phoneFilterRef}
                onChange={handleInputChange('phone')}
                placeholder="Поиск по номеру телефона"
              />
            </div>
          }
          style={{ width: '15%' }}
        />
        <Column
          columnKey="email"
          sortable
          field="email"
          header="E-mail"
          filter
          onFilterClear={() =>
            clearFilter(
              'email',
              filters,
              setFilters,
              emailFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={emailFilterRef}
                onChange={handleInputChange('email')}
                placeholder="Поиск по E-mail"
              />
            </div>
          }
          style={{ width: '18%' }}
        />
        <Column
          columnKey="isUser"
          sortable
          field="isUser"
          header="Пользователь"
          dataType="boolean"
          style={{ width: '7%' }}
          body={userBodyTemplate}
          filter
          onFilterClear={() =>
            clearFilter(
              'isUser',
              filters,
              setFilters,
              undefined,
              setPageNumber,
              setFirst
            )
          }
          filterElement={userRowFilterTemplate}
        />
        <Column
          columnKey="login"
          sortable
          field="login"
          header="Логин"
          filter
          onFilterClear={() =>
            clearFilter(
              'login',
              filters,
              setFilters,
              loginFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={loginFilterRef}
                onChange={handleInputChange('login')}
                placeholder="Поиск по логину"
              />
            </div>
          }
          style={{ width: '18%' }}
        />
      </DataTable>
    </div>
  );
}
